import { ArrowRightLeft, Scissors } from "lucide-react";
import { useState } from "react";
import { useReassignSubscription, useSplitSubscription } from "../../api/admin";
import { AdminSubscription } from "../../types/admin";
import { notify } from "./notify";
import { Button, Field } from "./ui";

type Mode = "reassign" | "split";

const MODES: Record<Mode, { label: string; help: string }> = {
  reassign: {
    label: "Réaffecter",
    help: "Tout l'abonnement passe à l'autre email. Le livre déjà commencé reste chez l'utilisateur actuel.",
  },
  split: {
    label: "Séparer 1 livre",
    help: "Pour un achat en quantité : un livre est retiré de cet abonnement et un nouvel abonnement (même commande Shopify) est créé pour l'autre email.",
  },
};

/**
 * Moves a subscription, or one book of a purchase made in quantity, to
 * another email. Unknown emails get a new invited account.
 */
export const SubscriptionTransfer = ({
  subscription,
}: {
  subscription: AdminSubscription;
}) => {
  const reassign = useReassignSubscription();
  const split = useSplitSubscription();
  const [mode, setMode] = useState<Mode | null>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [sendInvitation, setSendInvitation] = useState(true);
  const [force, setForce] = useState(false);

  // On a print purchase the books are copies of the same memoir
  const canSplit =
    subscription.product === "pdf" &&
    subscription.books_purchased >= 2 &&
    subscription.books_remaining >= 1;
  const isLoading = split.isLoading || reassign.isLoading;

  const reset = () => {
    setMode(null);
    setEmail("");
    setFirstName("");
    setLastName("");
    setForce(false);
  };

  const submit = () => {
    const variables = {
      id: subscription.id,
      params: {
        email,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        send_invitation: sendInvitation,
        force,
      },
    };
    if (mode === "split") {
      split.mutate(
        variables,
        notify(`Un livre a été attribué à ${email}`, reset),
      );
    } else {
      reassign.mutate(
        variables,
        notify(`Abonnement réaffecté à ${email}`, reset),
      );
    }
  };

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setMode("reassign")}>
          <ArrowRightLeft className="w-3.5 h-3.5" /> Réaffecter à un autre email
        </Button>
        {canSplit && (
          <Button size="sm" onClick={() => setMode("split")}>
            <Scissors className="w-3.5 h-3.5" /> Séparer 1 livre vers un autre
            email
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <p className="text-xs text-gray-600">
        <strong>{MODES[mode].label}</strong> — {MODES[mode].help}
      </p>
      <Field
        label="Email du destinataire"
        type="email"
        value={email}
        onChange={setEmail}
      />
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Prénom (si nouveau compte)"
          value={firstName}
          onChange={setFirstName}
        />
        <Field
          label="Nom (si nouveau compte)"
          value={lastName}
          onChange={setLastName}
        />
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-gray-700">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={sendInvitation}
            onChange={(e) => setSendInvitation(e.target.checked)}
            className="rounded border-gray-300"
          />
          Envoyer l'invitation si le compte est nouveau
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="rounded border-gray-300"
          />
          Forcer même si ce compte a déjà un abonnement
        </label>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={submit}
          disabled={isLoading || !email}
          confirm={`${MODES[mode].label} vers ${email} ?`}
        >
          Valider
        </Button>
        <Button size="sm" onClick={reset}>
          Annuler
        </Button>
      </div>
    </div>
  );
};
